import {
  type TableFeatures,
  type RowData,
  type Table_Internal,
  type Row,
  type Table,
} from '@tanstack/react-table';
import {
  table_getRowCount,
  row_getIsSelected,
} from '@tanstack/react-table/static-functions';
import {
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type JSX,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';

import {
  row_getIsAllSubRowsSelected,
  row_getIsSomeSelected,
} from './RowSelection.js';
import { Checkbox } from '../../../../forms/checkbox/Checkbox.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { dataTableMessages } from '../../messages.js';

/**
 * DataTable row selection header.
 * @internal
 */
export function RowSelectionHeader<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  props: Readonly<{
    table: Table<TFeatures, TData>;
  }>,
) {
  const resetBatchSelection = props.table.resetBatchSelection;
  const value = getCheckboxValue(props.table);
  const isDisabled = isSelectAllDisabled(props.table);

  const intl = useIntl();
  const checkboxMessage = intl.formatMessage(
    dataTableMessages.selectAllTooltip,
    {
      nextSelection:
        value === false || value === 'indeterminate' ? 'select' : 'deselect',
      scope: props.table.options.rowSelectionSelectAllMode,
    },
  );

  useEffect(() => {
    const controller = new AbortController();
    document.addEventListener('click', resetBatchSelection(), {
      signal: controller.signal,
    });
    return () => {
      controller.abort();
    };
  }, [resetBatchSelection]);

  function toggleSelectAll(
    event: ChangeEvent<HTMLInputElement> | undefined,
  ): void {
    if (isDisabled) {
      event?.preventDefault();
      return;
    }
    if (props.table.options.rowSelectionSelectAllMode === 'page') {
      if (props.table.options.rowSelectionLimit === undefined) {
        // Use our overridden toggleAllPageRowsSelected instead of TanStack's
        // getToggleAllPageRowsSelectedHandler, which calls the module-level
        // function and bypasses our disabled-row guard.
        props.table.toggleAllPageRowsSelected(
          (event?.target as HTMLInputElement | null)?.checked,
        );
      } else {
        props.table.getToggleAllLimitedPageRowsSelectedHandler()(event);
      }
    } else if (props.table.options.rowSelectionSelectAllMode === 'all') {
      if (props.table.options.rowSelectionLimit === undefined) {
        // Same as above for the 'all' mode.
        props.table.toggleAllRowsSelected(
          (event?.target as HTMLInputElement | null)?.checked,
        );
      } else {
        props.table.getToggleAllLimitedRowsSelectedHandler()(event);
      }
    }
  }

  const selectAllDtProperties = `state:${
    value === true ? 'deselect' : 'select'
  }`;

  const checkbox = (
    <Checkbox
      value={isDisabled ? false : value}
      aria-disabled={isDisabled}
      aria-label={checkboxMessage}
      onChange={(_, event) => toggleSelectAll(event)}
      data-dt-name="RowSelectDeselectAll"
      data-dt-properties={selectAllDtProperties}
    />
  );

  return (
    <DataTableDefaultCell>
      {isDisabled ? (
        checkbox
      ) : (
        <Tooltip text={checkboxMessage}>{checkbox}</Tooltip>
      )}
    </DataTableDefaultCell>
  );
}

/**
 * @returns true, if the 'select all' checkbox shall be disabled, false otherwise.
 */
function isSelectAllDisabled<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table<TFeatures, TData>): boolean {
  const isTableEmpty =
    table_getRowCount(table as Table_Internal<TFeatures, TData>) === 0;
  if (isTableEmpty) {
    return true;
  }
  let isAllRowsSelectionDisabled = false;
  if (table.options.rowSelectionSelectAllMode === 'page') {
    isAllRowsSelectionDisabled = table.getIsAllPageRowsSelectionDisabled();
  } else if (table.options.rowSelectionSelectAllMode === 'all') {
    isAllRowsSelectionDisabled = table.getIsAllRowsSelectionDisabled();
  }
  return isAllRowsSelectionDisabled;
}

/**
 * @returns the current state of the 'select all' checkbox
 */
function getCheckboxValue<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table<TFeatures, TData>): boolean | 'indeterminate' {
  if (table.options.rowSelectionSelectAllMode === 'page') {
    if (table.getIsAllPageRowsSelected()) {
      return true;
    }
    if (table.getIsSomePageRowsSelected()) {
      return 'indeterminate';
    }
    return false;
  }
  if (table.options.rowSelectionSelectAllMode === 'all') {
    if (table.getIsAllRowsSelected()) {
      return true;
    }
    if (table.getIsSomeRowsSelected()) {
      return 'indeterminate';
    }
    return false;
  }
  return false;
}

function resolveCheckboxValue<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean | 'indeterminate' {
  if (row.subRows.length === 0) {
    return row_getIsSelected(row);
  }

  if (row_getIsAllSubRowsSelected(row)) {
    return true;
  }

  if (row_getIsSomeSelected(row)) {
    return 'indeterminate';
  }

  return false;
}

/**
 * DataTable row selection cell.
 * @internal
 */
export function RowSelectionCell<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  props: Readonly<{
    row: Row<TFeatures, TData>;
  }>,
): JSX.Element {
  const intl = useIntl();

  const isDisabled = props.row.getShouldDisableRowSelection();
  const rowDtProperties = `state:${
    resolveCheckboxValue(props.row) === true ? 'deselect' : 'select'
  }`;

  return (
    <DataTableDefaultCell
      onMouseDown={(event: ReactMouseEvent) => {
        document.getSelection()?.removeAllRanges();
        event.preventDefault();
      }}
    >
      <Checkbox
        aria-label={intl.formatMessage(dataTableMessages.selectRowCheckbox, {
          id: props.row.id,
        })}
        value={resolveCheckboxValue(props.row)}
        aria-disabled={isDisabled}
        onChange={(value, event) => {
          if (isDisabled) {
            event?.preventDefault();
            return;
          }
          props.row.batchSelect(
            value,
            (event?.nativeEvent as MouseEvent).shiftKey,
          );
        }}
        data-row-selection-checkbox
        data-dt-name="RowSelectDeselect"
        data-dt-properties={rowDtProperties}
      />
    </DataTableDefaultCell>
  );
}
